import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotifications,
  markAllNotificationsAsRead,
} from '@/api/notifications';
import { toast } from 'sonner';
import { Notification } from '@/types';
import { notificationSocket } from '@/api/client/socket';
import { useAuth } from '@/providers/AuthProvider';
import { handleAPIError } from '@/utils/errorHandler';

export const useNotifications = () => {
  const queryClient = useQueryClient();
  const { organization, user } = useAuth();
  const orgId = organization?.id;
  const userId = user?.id;

  const query = useQuery<Notification[]>({
    queryKey: ['notifications', orgId, userId],
    queryFn: getNotifications,
    enabled: !!orgId && !!userId,
    staleTime: 1 * 60 * 1000,
  });

  useEffect(() => {
    if (!user || !organization) return;

    if (!notificationSocket.connected) {
      notificationSocket.connect();
    }

    // ── User-specific notification ────────────────────────────────────────
    const handleNewNotification = (newNotification: Notification) => {
      toast.info(newNotification.message);

      queryClient.setQueryData<Notification[]>(
        ['notifications', orgId, userId],
        (oldData = []) => [newNotification, ...oldData],
      );
    };

    // ── Org-wide broadcast ────────────────────────────────────────────────
    const handleOrgNotification = (data: { message: string }) => {
      toast.info(data.message);

      queryClient.setQueryData<Notification[]>(
        ['notifications', orgId, userId],
        (oldData = []) => [
          {
            id: `org-${Date.now()}`,
            title: 'System Alert',
            message: data.message,
            isRead: false,
            createdAt: new Date().toISOString(),
            link: undefined,
          },
          ...oldData,
        ],
      );
    };

    notificationSocket.on('new_notification', handleNewNotification);
    notificationSocket.on('org_notification', handleOrgNotification);

    return () => {
      notificationSocket.off('new_notification', handleNewNotification);
      notificationSocket.off('org_notification', handleOrgNotification);
    };
  }, [queryClient, user, organization, orgId, userId]);

  const unreadCount = query.data?.filter((n) => !n.isRead).length ?? 0;

  return {
    notifications: query.data ?? [],
    unreadCount,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useReadNotifications = () => {
  const queryClient = useQueryClient();
  const { organization, user } = useAuth();
  const orgId = organization?.id;
  const userId = user?.id;

  const mutation = useMutation({
    mutationFn: () => markAllNotificationsAsRead(),
    onSuccess: () => {
      // Mark all as read in cache
      queryClient.setQueryData<Notification[]>(
        ['notifications', orgId, userId],
        (oldData = []) =>
          oldData.map((n) => (n.isRead ? n : { ...n, isRead: true })),
      );
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  const clearAllNotifications = () => {
    queryClient.setQueryData<Notification[]>(
      ['notifications', orgId, userId],
      [],
    );
    mutation.mutate();
  };

  return {
    markReadNotification: mutation.mutate,
    markReadNotificationAsync: mutation.mutateAsync,
    clearAllNotifications,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
